import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { trackDashboardEvent } from '@modules/dashboard/helpers';
import { useUpsertDashboard } from '@modules/dashboard/hooks';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import { DashboardCollaborator } from '@modules/dashboard/types';
import { PersonAddAltRounded } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { alpha, Box, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { DashboardShareByEmailDropdown } from '../DashboardShareByEmailDropdown';
import { DashboardShareCollaboratorList } from '../DashboardShareCollaboratorList';

interface DashboardShareDrawerInviteCollaboratorsProps {
  closeDrawer: () => void;
}

export const DashboardShareDrawerInviteCollaborators = ({
  closeDrawer,
}: DashboardShareDrawerInviteCollaboratorsProps) => {
  const [editorsToAdd, setEditorsToAdd] = useState<DashboardCollaborator[]>([]);
  const dashboard = useDashboardStore(({ dashboard }) => dashboard);

  const { showSuccessNotification, showErrorNotification } =
    useNotificationContext();

  const usersToInvite = editorsToAdd
    .filter((email) => !email.isExisting && email.isValidEmail)
    .map((email) => email.email);

  const existingUserIDs = editorsToAdd
    .map((editor) => (editor.isExisting ? editor.id : ''))
    .filter(Boolean);

  const { upsertDashboard, loading } = useUpsertDashboard();

  if (!dashboard) {
    return null;
  }

  const addEditors = async () => {
    if (editorsToAdd.length === 0) {
      return;
    }

    try {
      await upsertDashboard({
        dashboardProps: {
          title: dashboard?.title,
          scope: dashboard?.scope,
          shortMetadata: dashboard?.shortMetadata,
          editorsToAdd: existingUserIDs,
          editorsToInvite: usersToInvite,
        },
      });
      showSuccessNotification({
        contentComponent: `"${dashboard.title}" has been shared successfully! Collaborators can edit the Dashboard or invite others.`,
        props: { isClosable: true },
      });
      trackDashboardEvent({
        eventName: 'Dashboard Collaborators Added',
        dashboardLink: window.location.href,
        collaborators: editorsToAdd,
      });
    } catch {
      showErrorNotification({
        contentComponent: 'Some error occurred while sharing.',
        props: { isClosable: true },
      });
    }
    closeDrawer();
    setEditorsToAdd([]);
  };

  const isInviteDisabled =
    editorsToAdd.length === 0 ||
    !!editorsToAdd.find((editor) => !editor.isExisting && !editor.isValidEmail);

  return (
    <Stack direction="row" spacing={1} px={4}>
      <Stack
        bgcolor={(theme) => alpha(theme.palette.primary.main, 0.08)}
        width={36}
        height={36}
        alignItems="center"
        justifyContent="center"
        borderRadius="50%"
        minWidth={36}
      >
        <PersonAddAltRounded color="primary" />
      </Stack>
      <Stack spacing={2} width="fit-content">
        <Typography marginTop={0.5}>Invite to Collaborate</Typography>
        <Typography variant="caption" color="text.disabled">
          Enter the Email IDs of teammates who you'd like to help build this
          Dashboard. They will be able to add other collaborators.
        </Typography>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box width="100%">
              <DashboardShareByEmailDropdown
                setEditorsToAdd={setEditorsToAdd}
                editorsToAdd={editorsToAdd}
              />
            </Box>
            <LoadingButton
              variant="contained"
              disabled={isInviteDisabled}
              onClick={addEditors}
              loading={loading}
              sx={{ height: 'fit-content' }}
            >
              Invite
            </LoadingButton>
          </Stack>
          {usersToInvite.length !== 0 && (
            <Typography color="info.main" variant="caption">
              {usersToInvite.join(', ')} will be invited to join Enterpret and
              collaborate on this Dashboard.
            </Typography>
          )}
          <DashboardShareCollaboratorList />
        </Box>
      </Stack>
    </Stack>
  );
};
